/**
 * features/finance/AgeAnalysisCheck.tsx
 * ------------------------------------------------------------------
 * FINANCE — "Pastel Check": upload Pastel's Customer Detailed Ledger and
 * see every pupil whose balance there differs from the portal's.
 *
 * Route `#/financeAgeAnalysis` (the name is from when it took the age
 * analysis), gated by canImportPastel (the bursar and finance managers).
 * The Saturday 11:30 reminder — bell, push, email — opens it directly.
 *
 * THE DETAILED LEDGER, NOT THE AGE ANALYSIS (17 Oct 2026). The school
 * asked for one weekly upload that lets the portal be kept in step with
 * Pastel entry by entry. The ledger (lib/pastelLedger) gives each
 * account's closing balance — the figure compared — and every document
 * behind it, so a card can show Pastel's entries since the pupil's
 * opening balance and offer each Pastel receipt the portal lacks as a
 * ready-made payment. A portal payment Pastel has no receipt for is
 * flagged on its card — who entered it, when — with "reverse it" and
 * "keep it" beside it: the screen IS the verification list, nothing is
 * sent separately. Every reversal and void made to match Pastel is
 * logged, and the second tab ("Changes made to match Pastel",
 * PastelChanges) lists them with an Undo on each (18 Oct 2026). An age analysis is refused with the way to the
 * right report. Uploads saved before then still show, without entries.
 *
 * It reads the export, sets each row beside
 * `accountStandings()` — the same figure every other finance screen
 * shows — and lists what disagrees. Each upload is logged, WITH its rows,
 * in financeAgeAnalysisUploads, so the latest file stays on screen across
 * visits and can be worked through over days.
 *
 * FIX CARDS (Oct 2026). Every disagreement is a card with the fixes that
 * fit it (lib/reconFixes): link the Pastel code, use Pastel's spelling,
 * void an invoice Pastel never charged, record a payment Pastel has, raise
 * a charge the portal is missing, or note why the portal is right. The
 * writes are in reconFixWrite and go through the portal's ordinary finance
 * paths; HIGH-RISK-CHANGES.md §8. The comparison is live, so a card goes
 * as soon as its difference closes. The table view is still there.
 *
 * The portal side is the balance NOW. Pastel's side is whatever date the
 * export was run to — a payment captured in one ledger and not yet the
 * other shows up as a difference, which is exactly the point.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import {
  Scale, Upload, Loader2, Search, Download, History, AlertTriangle, LayoutGrid, Table2,
} from 'lucide-react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db, addDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { GradientHero, SoftCard, StatChip, glassFieldClass, btnCsv } from '@/features/finance/ui';
import UploadReminderRecipients from '@/features/notifications/UploadReminderRecipients';
import PastelFixCard, { type FixRunners } from '@/features/finance/PastelFixCard';
import PastelChanges from '@/features/finance/PastelChanges';
import {
  RECON_NOTES_COLLECTION, linkPastelCode, noteDifference, noteKey, raiseReconInvoice, recordReconPayment,
  addPastelPupil, logReconChange, renamePupil, reverseReconPayment, voidInvoice, type ReconActor, type ReconNote,
} from '@/features/finance/reconFixWrite';
import { fixesFor, leftFlag, ledgerEvidence, portalFlags, type Fix, type LedgerEvidence } from '@/lib/reconFixes';
import { SCHOOL } from '@/lib/school.config';
import { accountStandings, fmtBalance } from '@/lib/financeCore';
import { readFirstSheet } from '@/lib/readSheet';
import { UPLOAD_REMINDERS } from '@/lib/uploadReminders';
import {
  compareAgeAnalysis, isPastelAgeAnalysis,
  type ComparisonSummary, type DiffKind, type DiffRow, type PastelRow,
} from '@/lib/pastelAgeAnalysis';
import {
  isPastelLedger, ledgerEnd, ledgerRows, linesByCode, openingBaseDate, parsePastelLedger, storeLines,
  type StoredLine,
} from '@/lib/pastelLedger';
import { isOpeningBalance } from '@/lib/financeCore';

/** Where the report is, in Pastel 19, for every message that asks for it. */
const LEDGER_PATH = 'View → Customers → Detailed Ledger → By Customer';

/** One upload, read and ready to compare. */
interface ParsedUpload {
  title: string;
  rows: PastelRow[];
  total: number;
  warnings: string[];
  lines: StoredLine[];
}

const UPLOADS_COLLECTION = UPLOAD_REMINDERS.ageAnalysis.uploadsCollection;

const KIND_LABEL: Record<DiffKind, string> = {
  differs: 'Balances differ',
  missing_in_portal: 'Not found in portal',
  missing_in_pastel: 'Not in Pastel',
  name_only: 'Matched by name only',
  agree: 'Agree',
};
const KIND_BADGE: Record<DiffKind, string> = {
  differs: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  missing_in_portal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  missing_in_pastel: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  name_only: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  agree: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};
const KIND_HELP: Record<DiffKind, string> = {
  differs: 'Both ledgers know the pupil but carry different balances — look for a payment, credit or invoice captured in only one.',
  missing_in_portal: 'Pastel has this customer code and no pupil in the portal carries it, nor shares the name.',
  missing_in_pastel: 'The portal shows a balance for a pupil with no Pastel account (or none recorded on the pupil).',
  name_only: 'No pupil carries this Pastel code, but exactly one uncoded pupil has the same name. Record the code on the pupil to make the match firm.',
  agree: 'Both ledgers agree to within 50 thebe.',
};

interface UploadLog {
  id: string;
  uploadedAt: string;
  uploadedByName: string;
  fileName: string;
  counts: Record<DiffKind, number>;
  absoluteDifference: number;
  /** Every row of the file (saved from Oct 2026), so the cards survive a reload. */
  rows?: PastelRow[];
  /** The Detailed Ledger's entries from July (lib/pastelLedger storeLines), from 17 Oct 2026. */
  lines?: StoredLine[];
}

/** What kind of work a card is, for the filter chips. */
type CardGroup = 'verify' | 'link' | 'charge' | 'payment' | 'name' | 'notfound' | 'dormant' | 'notinpastel';
const GROUP_LABEL: Record<CardGroup, string> = {
  verify: 'Portal entry not in Pastel',
  link: 'Link Pastel code',
  charge: 'Charge missing in portal',
  payment: 'Payment or invoice to correct',
  name: 'Name spelt differently',
  notfound: 'No pupil found',
  dormant: 'Old Pastel accounts',
  notinpastel: 'Not in Pastel',
};
function groupOf(r: DiffRow, fixes: Fix[], activity: number | undefined, flagged: boolean): CardGroup {
  const has = (k: Fix['kind']) => fixes.some((f) => f.kind === k);
  // Someone's portal entry that Pastel does not carry: it needs a person, not a rule.
  if (flagged) return 'verify';
  if (has('linkCode')) return 'link';
  if (has('raiseInvoice')) return 'charge';
  if (has('voidInvoice') || has('recordPayment')) return 'payment';
  if (has('useName')) return 'name';
  // Nothing on the account all year and no pupil: an alumnus or an old debt.
  if (r.kind === 'missing_in_portal') return activity === 0 ? 'dormant' : 'notfound';
  return 'notinpastel';
}

export default function AgeAnalysisCheck() {
  const { userData } = useAuth();
  const { students, classById } = useData();
  const fin = useFinanceData();
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedUpload | null>(null);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [history, setHistory] = useState<UploadLog[]>([]);
  const [kindFilter, setKindFilter] = useState<DiffKind | 'issues'>('issues');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [group, setGroup] = useState<CardGroup | 'all'>('all');
  const [showNoted, setShowNoted] = useState(false);
  const [cardLimit, setCardLimit] = useState(40);
  const [notes, setNotes] = useState<Map<string, ReconNote>>(new Map());
  const [tab, setTab] = useState<'differences' | 'changes'>('differences');

  useEffect(() => onSnapshot(
    collection(db, RECON_NOTES_COLLECTION),
    (snap) => setNotes(new Map(snap.docs.map((d) => [d.id, d.data() as ReconNote]))),
    () => setNotes(new Map()),
  ), []);

  useEffect(() => onSnapshot(
    query(collection(db, UPLOADS_COLLECTION), orderBy('uploadedAt', 'desc'), limit(8)),
    (snap) => setHistory(snap.docs.map((d) => ({ ...(d.data() as UploadLog), id: d.id }))),
    () => setHistory([]),
  ), []);

  const standings = useMemo(() => accountStandings(fin.invoices), [fin.invoices]);

  /* The file on screen: one just picked, else the latest saved check, so the
     cards are still there tomorrow. */
  const latestSaved = history.find((h) => Array.isArray(h.rows) && h.rows.length > 0);
  const activeRows = parsed ? parsed.rows : latestSaved?.rows ?? null;
  const activeFile = parsed ? fileName : latestSaved?.fileName ?? '';
  // The ledger's entries, by Pastel code — absent for an older age-analysis upload.
  const activeLines = parsed ? parsed.lines : latestSaved?.lines;
  const ledgerLines = useMemo(() => (activeLines ? linesByCode(activeLines) : null), [activeLines]);
  // The last day the file covers: a portal entry after it is not "missing from Pastel".
  const until = useMemo(() => (ledgerLines ? ledgerEnd(ledgerLines.values()) : undefined), [ledgerLines]);
  const activityByCode = useMemo(() => new Map((activeRows ?? []).map((r) => [r.code, r.activity])), [activeRows]);

  const comparison = useMemo<ComparisonSummary | null>(() => {
    if (!activeRows || fin.loading) return null;
    return compareAgeAnalysis(
      activeRows,
      students.map((s) => ({
        id: s.id, name: s.fullName, pastelCode: s.pastelCode, status: s.status,
        className: classById(s.currentClass)?.name ?? (s.status === 'pending' ? 'Pending' : ''),
      })),
      (id) => standings.get(id)?.balance ?? 0,
    );
  }, [activeRows, fin.loading, students, classById, standings]);

  // Every upload is logged once its comparison exists — that log is what
  // the Saturday reminder reads, and the history below.
  useEffect(() => {
    if (!comparison || !parsed || recorded) return;
    setRecorded(true);
    // Inside a promise, so a document Firestore refuses before it is even
    // sent (it throws, rather than rejecting) shows as a message, not a crash.
    Promise.resolve().then(() => addDoc(collection(db, UPLOADS_COLLECTION), {
      uploadedAt: new Date().toISOString(),
      uploadedBy: userData?.uid ?? 'unknown',
      uploadedByName: userData?.displayName || userData?.email || 'unknown',
      fileName,
      title: parsed.title,
      rowCount: parsed.rows.length,
      pastelTotal: comparison.pastelTotal,
      portalTotal: comparison.portalTotal,
      absoluteDifference: comparison.absoluteDifference,
      counts: comparison.counts,
      duplicateCodes: comparison.duplicateCodes,
      // The disagreements only, capped well inside a document's 1 MB.
      differences: comparison.rows.filter((r) => r.kind !== 'agree').slice(0, 1500),
      // The whole file, so the fix cards can be worked through over days.
      rows: parsed.rows,
      source: 'detailedLedger',
      lines: parsed.lines,
    })).catch((e) => setError(`The comparison is shown but could not be saved to history: ${(e as Error).message}`));
  }, [comparison, parsed, recorded, fileName, userData]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setReading(true);
    setError('');
    setParsed(null);
    setRecorded(false);
    try {
      const rows = await readFirstSheet(f);
      if (isPastelAgeAnalysis(rows)) {
        throw new Error(`This is the age analysis. The Pastel Check now takes the Customer Detailed Ledger instead — in Pastel: ${LEDGER_PATH}, all customers, from 1 January to today, exported to Excel.`);
      }
      if (!isPastelLedger(rows)) throw new Error(`This is not Pastel's Customer Detailed Ledger. In Pastel: ${LEDGER_PATH}, exported to Excel.`);
      const ledger = parsePastelLedger(rows);
      if (!ledger.accounts.length) throw new Error('No customer accounts found in the file.');
      setFileName(f.name);
      setParsed({ title: ledger.title, rows: ledgerRows(ledger), total: ledger.total, warnings: ledger.warnings, lines: storeLines(ledger) });
      setKindFilter('issues');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setReading(false);
    }
  }

  const shown = useMemo(() => {
    if (!comparison) return [];
    const q = search.trim().toLowerCase();
    return comparison.rows
      .filter((r) => (kindFilter === 'issues' ? r.kind !== 'agree' : r.kind === kindFilter))
      .filter((r) => !q || `${r.code} ${r.pastelName} ${r.portalName} ${r.className}`.toLowerCase().includes(q))
      .sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
  }, [comparison, kindFilter, search]);

  function exportCsv() {
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [
      ['Status', 'Pastel code', 'Pastel name', 'Portal name', 'Class', 'Pastel category', 'Pastel balance', 'Portal balance', 'Difference (portal - Pastel)'].map(esc).join(','),
      ...shown.map((r) => [KIND_LABEL[r.kind], r.code, r.pastelName, r.portalName, r.className, r.category, r.pastel.toFixed(2), r.portal.toFixed(2), r.difference.toFixed(2)].map(esc).join(',')),
    ];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `pastel-vs-portal-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const issues = comparison ? comparison.rows.length - comparison.counts.agree : 0;

  /* ---------------- fix cards ---------------- */
  const actor: ReconActor = { id: userData?.uid ?? 'unknown', name: userData?.displayName || userData?.email || 'unknown', email: userData?.email };
  const invoicesByStudent = useMemo(() => {
    const m = new Map<string, typeof fin.invoices>();
    for (const i of fin.invoices) if (i.status !== 'void') (m.get(i.studentId) ?? m.set(i.studentId, []).get(i.studentId)!).push(i);
    return m;
  }, [fin.invoices]);
  const reconStudents = useMemo(() => students.map((s) => ({
    id: s.id, name: s.fullName, pastelCode: s.pastelCode, className: classById(s.currentClass)?.name, status: s.status,
  })), [students, classById]);
  const uncodedPupils = useMemo(() => reconStudents.filter((s) => !s.pastelCode), [reconStudents]);

  // Money that really arrived — a reversed payment and its reversal answer no
  // Pastel receipt — and, apart, the payments someone reversed.
  const { paymentsByStudent, reversedByStudent } = useMemo(() => {
    const live = new Map<string, typeof fin.payments>();
    const reversed = new Map<string, typeof fin.payments>();
    for (const p of fin.payments) {
      if (p.isReversal) continue;
      const m = p.reversed ? reversed : live;
      (m.get(p.studentId) ?? m.set(p.studentId, []).get(p.studentId)!).push(p);
    }
    return { paymentsByStudent: live, reversedByStudent: reversed };
  }, [fin.payments]);

  const cards = useMemo(() => {
    if (!comparison) return [];
    return comparison.rows
      .map((r) => {
        const invoices = invoicesByStudent.get(r.studentId ?? '') ?? [];
        // With the ledger: Pastel's entries since this pupil's opening balance, and its receipts the portal lacks.
        const account = ledgerLines && r.code ? ledgerLines.get(r.code) ?? [] : null;
        const since = openingBaseDate(invoices.filter(isOpeningBalance).map((i) => i.lines?.[0]?.label ?? ''));
        const entries = account && r.studentId ? account.filter((l) => l.date > since) : undefined;
        const ev: LedgerEvidence = account && r.studentId
          ? ledgerEvidence({
            difference: r.difference, lines: account, since, invoices,
            payments: paymentsByStudent.get(r.studentId) ?? [], reversedPayments: reversedByStudent.get(r.studentId) ?? [], until,
          })
          : {};
        ev.activity = activityByCode.get(r.code);
        const flags = portalFlags(ev);
        const fixes = fixesFor(r, invoices, reconStudents, ev);
        const note = notes.get(noteKey(r));
        const noted = !!note && Math.abs(note.difference - r.difference) < 0.5;
        return { r, fixes, noted, entries, since, flags, group: groupOf(r, fixes, activityByCode.get(r.code), flags.length > 0), flag: leftFlag(r, reconStudents) };
      })
      .filter((c) => c.fixes.length > 0)
      // Someone's portal entry that Pastel does not have comes first: it needs a person, not a rule.
      .sort((a, b) => Number(b.group === 'verify') - Number(a.group === 'verify') || Math.abs(b.r.difference) - Math.abs(a.r.difference));
  }, [comparison, invoicesByStudent, reconStudents, notes, ledgerLines, until, paymentsByStudent, reversedByStudent, activityByCode]);

  const openCards = cards.filter((c) => showNoted || !c.noted);
  const groupCounts = openCards.reduce((m, c) => { m[c.group] = (m[c.group] ?? 0) + 1; return m; }, {} as Partial<Record<CardGroup, number>>);
  const shownCards = openCards
    .filter((c) => group === 'all' || c.group === group)
    .filter((c) => {
      const q = search.trim().toLowerCase();
      return !q || `${c.r.code} ${c.r.pastelName} ${c.r.portalName} ${c.r.className}`.toLowerCase().includes(q);
    });
  const notedCount = cards.filter((c) => c.noted).length;

  function runnersFor(row: DiffRow): FixRunners {
    const studentOf = (id?: string) => students.find((s) => s.id === id);
    return {
      linkCode: (f) => linkPastelCode(f.studentId!, f.code!, actor, activeFile),
      useName: (f, name) => renamePupil(f.studentId!, studentOf(f.studentId)?.fullName ?? row.portalName, name, actor, activeFile),
      voidInvoice: async (f, reason) => {
        const inv = fin.invoices.find((i) => i.id === f.invoiceId);
        if (!inv) throw new Error('That invoice is no longer there.');
        await voidInvoice(inv, reason, actor, activeFile);
        await logReconChange({
          batch: 'card', kind: 'voidInvoice', studentId: inv.studentId, studentName: inv.studentName, pastelCode: row.code || undefined,
          amount: Number(inv.total) || 0, invoiceId: inv.id, invoiceNo: inv.invoiceNo, beforeStatus: inv.status,
          originalAt: inv.issuedAt, reason, pastelFile: activeFile,
        }, actor);
      },
      recordPayment: async (_f, p) => {
        const inv = fin.invoices.find((i) => i.id === p.invoiceId);
        if (!inv) throw new Error('Choose the invoice the payment is against.');
        await recordReconPayment({ invoice: inv, ...p, paymentCount: fin.payments.length, actor, file: activeFile });
      },
      raiseInvoice: async (f, p) => {
        const st = studentOf(f.studentId);
        if (!st) throw new Error('That pupil is no longer there.');
        const year = SCHOOL.academicYearDefault;
        await raiseReconInvoice({
          student: { id: st.id, fullName: st.fullName, familyId: st.familyId },
          ...p, year, settings: fin.settings,
          invoicesThisYear: fin.invoices.filter((i) => i.year === year).length,
          actor, file: activeFile,
        });
      },
      reversePayment: async (f, reason) => {
        const p = fin.payments.find((x) => x.id === f.paymentId);
        if (!p) throw new Error('That payment is no longer there.');
        const inv = fin.invoices.find((i) => i.id === p.invoiceId);
        const revId = await reverseReconPayment(p, inv, reason, actor, activeFile);
        await logReconChange({
          batch: 'card', kind: 'reversePayment', studentId: p.studentId, studentName: p.studentName, pastelCode: row.code || undefined,
          amount: p.amount, invoiceId: p.invoiceId, invoiceNo: inv?.invoiceNo, paymentId: p.id, reversalPaymentId: revId,
          receiptNo: p.receiptNo, originalBy: p.recordedByName, originalAt: p.receivedAt, reason, pastelFile: activeFile,
        }, actor);
      },
      linkPupil: async (f, studentId) => {
        await linkPastelCode(studentId, f.code!, actor, activeFile);
        await logReconChange({
          batch: 'card', kind: 'linkPupil', studentId, studentName: studentOf(studentId)?.fullName, pastelCode: f.code,
          amount: row.pastel, invoiceId: '', reason: `Pastel ${f.code} (${row.pastelName}) is this pupil`, pastelFile: activeFile,
        }, actor);
      },
      addPupil: async (f, name) => {
        const added = await addPastelPupil({
          name, code: f.code!, status: f.status ?? 'pending', balance: f.amount ?? 0,
          openingCount: fin.invoices.filter((i) => String(i.invoiceNo ?? '').startsWith('OPEN/2026/PC')).length,
          actor, file: activeFile,
        });
        await logReconChange({
          batch: 'card', kind: 'addPupil', studentId: added.studentId, studentName: name, pastelCode: f.code,
          amount: f.amount ?? 0, invoiceId: added.invoiceId ?? '', invoiceNo: added.invoiceNo,
          reason: `In Pastel as ${row.pastelName}; added ${f.status === 'left' ? 'as a former pupil' : 'pending enrolment'}`, pastelFile: activeFile,
        }, actor);
      },
      acknowledge: (note) => noteDifference(row, note, actor, activeFile),
    };
  }

  return (
    <div className="space-y-4">
      <GradientHero
        icon={Scale}
        title="Pastel check"
        subtitle={history[0]
          ? `Last checked ${new Date(history[0].uploadedAt).toLocaleString()} by ${history[0].uploadedByName}`
          : 'Compare Pastel’s customer ledger with the portal’s balances'}
      />

      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" role="tablist" aria-label="Pastel check">
        {([['differences', 'Differences'], ['changes', 'Changes made to match Pastel']] as const).map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-xs font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${tab === k
              ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
          >{label}</button>
        ))}
      </div>

      {tab === 'changes' && <PastelChanges payments={fin.payments} invoices={fin.invoices} actor={actor} />}

      {tab === 'differences' && (<>

      <UploadReminderRecipients kind="ageAnalysis" />

      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2"><Upload size={16} /> Upload the customer ledger</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
          The Customer Detailed Ledger. In Pastel: <b>{LEDGER_PATH}</b>, all customers, from 1 January to today,
          exported to Excel. Each card then shows Pastel’s entries for the pupil, and any Pastel receipt the portal
          is missing can be recorded with one click.
        </p>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={onPick}
          className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[var(--accent-strong)] file:text-white hover:file:opacity-90"
        />
        {(reading || (parsed && fin.loading)) && (
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> {reading ? 'Reading…' : 'Loading the portal’s balances…'}</p>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
        {parsed && parsed.warnings.length > 0 && (
          <details className="mt-2">
            <summary className="text-xs text-amber-700 dark:text-amber-300 cursor-pointer">{parsed.warnings.length} row{parsed.warnings.length === 1 ? '' : 's'} not read</summary>
            {parsed.warnings.slice(0, 30).map((w) => <p key={w} className="text-xs text-gray-500 dark:text-gray-400">{w}</p>)}
          </details>
        )}
      </SoftCard>

      {comparison && activeRows && (
        <>
          {!parsed && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Showing the latest saved check: <b>{activeFile}</b>. Upload a newer file to replace it.</p>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatChip tone="slate" value={activeRows.length} label={`Pastel accounts · ${fmtBalance(comparison.pastelTotal)}`} />
            <StatChip tone="emerald" value={comparison.counts.agree} label="Agree" />
            <StatChip tone={issues ? 'red' : 'emerald'} value={issues} label="Differences to look at" />
            <StatChip tone={comparison.absoluteDifference ? 'amber' : 'emerald'} value={fmtBalance(comparison.absoluteDifference)} label="Total size of the differences" />
          </div>

          {comparison.duplicateCodes.length > 0 && (
            <SoftCard hover={false}>
              <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                These Pastel codes are on more than one pupil in the portal, so only the first is compared: {comparison.duplicateCodes.join(', ')}.
              </p>
            </SoftCard>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" role="group" aria-label="View">
              {([['cards', 'Fix cards', LayoutGrid], ['table', 'Table', Table2]] as const).map(([k, label, Icon]) => (
                <button
                  key={k}
                  onClick={() => setView(k)}
                  aria-pressed={view === k}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${view === k
                    ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
                ><Icon size={13} /> {label}</button>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code, name or class" className={glassFieldClass('py-1 pl-8 w-56')} />
            </div>
          </div>

          {view === 'cards' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {(['all', 'verify', 'link', 'charge', 'payment', 'name', 'notfound', 'dormant', 'notinpastel'] as const).map((g) => (
                  <button
                    key={g}
                    onClick={() => { setGroup(g); setCardLimit(40); }}
                    aria-pressed={group === g}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border ${group === g
                      ? 'bg-[var(--accent-strong)] text-white border-transparent'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40'}`}
                  >
                    {g === 'all' ? `Everything to fix (${openCards.length})` : `${GROUP_LABEL[g]} (${groupCounts[g] ?? 0})`}
                  </button>
                ))}
                {notedCount > 0 && (
                  <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <input type="checkbox" checked={showNoted} onChange={(e) => setShowNoted(e.target.checked)} /> Show {notedCount} noted
                  </label>
                )}
              </div>
              <p className="text-[11px] text-gray-400">
                The highlighted button is the likeliest fix; hover any button for what it will do. Link codes first — a linked pupil is then compared on balance.
                Money fixes go through the ordinary ledger, receipts and audit log; invoices raised here are not emailed.
              </p>
              {shownCards.slice(0, cardLimit).map((c) => (
                <PastelFixCard
                  key={`${c.r.kind}_${c.r.code}_${c.r.studentId ?? ''}`}
                  row={c.r}
                  fixes={c.fixes}
                  invoices={invoicesByStudent.get(c.r.studentId ?? '') ?? []}
                  fileName={activeFile}
                  flag={c.flag}
                  entries={c.entries}
                  since={c.since}
                  portalFlags={c.flags}
                  pupils={uncodedPupils}
                  run={runnersFor(c.r)}
                />
              ))}
              {shownCards.length > cardLimit && (
                <button onClick={() => setCardLimit((n) => n + 40)} className="w-full text-sm font-medium text-[var(--accent-link)] py-2 hover:underline">
                  Show more ({shownCards.length - cardLimit} left)
                </button>
              )}
              {shownCards.length === 0 && (
                <SoftCard hover={false}><p className="text-sm text-gray-500">Nothing to fix here.</p></SoftCard>
              )}
            </div>
          )}

          {view === 'table' && (
          <SoftCard pad="p-0" hover={false}>
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-2">
              {(['issues', 'differs', 'missing_in_portal', 'missing_in_pastel', 'name_only', 'agree'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setKindFilter(k)}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border ${kindFilter === k
                    ? 'bg-[var(--accent-strong)] text-white border-transparent'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40'}`}
                >
                  {k === 'issues' ? `All differences (${issues})` : `${KIND_LABEL[k]} (${comparison.counts[k]})`}
                </button>
              ))}
              <div className="flex-1" />
              <button onClick={exportCsv} disabled={!shown.length} className={btnCsv}><Download size={14} /> CSV</button>
            </div>
            {kindFilter !== 'issues' && (
              <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">{KIND_HELP[kindFilter]}</p>
            )}
            <div className="overflow-x-auto max-h-[34rem]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white dark:bg-gray-800">
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 px-4 font-medium">Pupil</th>
                    <th className="py-2 px-4 font-medium">Pastel code</th>
                    <th className="py-2 px-4 font-medium">Class</th>
                    <th className="py-2 px-4 font-medium text-right">Pastel</th>
                    <th className="py-2 px-4 font-medium text-right">Portal</th>
                    <th className="py-2 px-4 font-medium text-right">Difference</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {shown.map((r) => (
                    <tr key={`${r.kind}_${r.code}_${r.studentId ?? ''}`}>
                      <td className="py-1.5 px-4">
                        {r.studentId ? (
                          <a href={`#/studentProfile/${r.studentId}`} className="text-gray-800 dark:text-gray-100 hover:underline">{r.portalName}</a>
                        ) : <span className="text-gray-800 dark:text-gray-100">{r.pastelName}</span>}
                        {r.studentId && r.pastelName && r.pastelName.toUpperCase() !== r.portalName.toUpperCase() && (
                          <span className="block text-[11px] text-gray-400">Pastel: {r.pastelName}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-4 font-mono text-xs text-gray-600 dark:text-gray-300">{r.code || '—'}</td>
                      <td className="py-1.5 px-4 text-gray-600 dark:text-gray-300">{r.className || r.category || '—'}</td>
                      <td className="py-1.5 px-4 text-right tabular-nums">{r.kind === 'missing_in_pastel' ? '—' : fmtBalance(r.pastel)}</td>
                      <td className="py-1.5 px-4 text-right tabular-nums">{r.kind === 'missing_in_portal' ? '—' : fmtBalance(r.portal)}</td>
                      <td className={`py-1.5 px-4 text-right tabular-nums font-semibold ${Math.abs(r.difference) < 0.5 ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                        {r.difference > 0 ? '+' : ''}{fmtBalance(r.difference)}
                      </td>
                      <td className="py-1.5 px-4">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${KIND_BADGE[r.kind]}`}>{KIND_LABEL[r.kind]}</span>
                      </td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-sm text-gray-400">Nothing here.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100 dark:border-gray-700">
              Difference = portal − Pastel. Positive: the portal says the pupil owes more than Pastel does. Credits are shown in brackets.
            </p>
          </SoftCard>
          )}
        </>
      )}

      {history.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <h3 className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-semibold text-sm text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <History size={15} /> Recent checks
          </h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {history.map((h) => (
                <tr key={h.id}>
                  <td className="py-1.5 px-4 text-gray-600 dark:text-gray-300">{new Date(h.uploadedAt).toLocaleString()}</td>
                  <td className="py-1.5 px-4 text-gray-800 dark:text-gray-100">{h.uploadedByName}</td>
                  <td className="py-1.5 px-4 text-gray-500 text-xs truncate max-w-[12rem]">{h.fileName}</td>
                  <td className="py-1.5 px-4 text-right tabular-nums">
                    {(h.counts?.differs ?? 0) + (h.counts?.missing_in_portal ?? 0) + (h.counts?.missing_in_pastel ?? 0) + (h.counts?.name_only ?? 0)} differences
                  </td>
                  <td className="py-1.5 px-4 text-right tabular-nums">{fmtBalance(h.absoluteDifference ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SoftCard>
      )}
      </>)}
    </div>
  );
}
